import React from "react";
import { FiCheckCircle, FiAlertTriangle, FiInfo, FiX } from "react-icons/fi";

const ResultSummaryModal = ({
  isOpen,
  onClose,
  title = "Operation Results",
  subtitle,
  type = "info", // 'success', 'warning', 'info'
  items = [], // Array<{ label: string, count?: number, badgeType?: 'success'|'warning'|'neutral', details?: string[] }>
  note,
  actionButton,
}) => {
  if (!isOpen) return null;

  const typeIcons = {
    success: <FiCheckCircle className="w-6 h-6 text-emerald-600" />,
    warning: <FiAlertTriangle className="w-6 h-6 text-amber-600" />,
    info: <FiInfo className="w-6 h-6 text-indigo-600" />,
  };

  const badgeStyles = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
    error: "bg-rose-50 text-rose-700 border-rose-200",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-slate-100">{typeIcons[type] || typeIcons.info}</div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
                {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-5 space-y-4 max-h-[60vh] overflow-y-auto">
            {items.map((item, idx) => (
              <div key={idx} className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800">{item.label}</span>
                  {item.count !== undefined && (
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                        badgeStyles[item.badgeType || "neutral"]
                      }`}
                    >
                      {item.count}
                    </span>
                  )}
                </div>

                {item.details && item.details.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-slate-600 list-disc list-inside">
                    {item.details.map((detail, dIdx) => (
                      <li key={dIdx}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {note && (
              <div className="p-3 rounded-xl bg-blue-50/70 border border-blue-200 text-xs text-blue-800">
                {note}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            {actionButton}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultSummaryModal;
